import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Hosts } from '#api';
import { enrollmentCodeQueryKeys } from '#api/query-keys.ts';
import { useEnrollmentToken } from './useEnrollmentToken.ts';
import { useTimeout } from './useTimeout.ts';

/**
 * {@link useEnrollmentState} fetches an enrollment token for a host ID, and handles the state management to determine when the
 * token has been used successfully or has expired, polling for the token and taking care to remove the cache when done.
 */
export function useEnrollmentState(
  hostId: string,
  { onContinue, enabled }: { onContinue: () => void; enabled: boolean }
) {
  const [enrollSuccessful, setEnrollmentSuccessful] = useState(false);
  const { token, isExpired, removeTokenCache } = useEnrollmentToken(hostId, { enabled: !enrollSuccessful && enabled });
  const tokenExists = usePollForTokenExists(hostId, token);

  const done = useCallback(() => {
    // Clear the cache so re-enrolling gets a new fresh code
    removeTokenCache();
    onContinue();
  }, [onContinue, removeTokenCache]);

  const { start: doneAfterDelay } = useTimeout(() => done(), 3000);

  useEffect(() => {
    if (isExpired) {
      done();
    } else if (!tokenExists) {
      // Close page after 3 seconds to read the success message
      doneAfterDelay();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEnrollmentSuccessful(true);
    }
  }, [tokenExists, isExpired, onContinue, done, doneAfterDelay]);

  return { token, isExpired, enrollSuccessful, handleDone: done };
}

/**
 * Calls our api server to determine whether an enrollment code still exists in redis.
 * If it doesn't, it could mean either:
 *
 * 1) The code expired without being used
 * 2) The code was used to enroll a host
 */
function usePollForTokenExists(hostId: string, enrollmentCode = '') {
  const { data } = useQuery({
    // We assume a one-to-one mapping between hostID and enrollmentCode. It would be simpler to make this work though...
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: enrollmentCodeQueryKeys.check(enrollmentCode),
    queryFn: ({ signal }) => Hosts.checkEnrollmentToken(hostId, enrollmentCode, { signal }),
    enabled: Boolean(enrollmentCode),
    // Continue fetching until the token disappears
    refetchInterval: (query) => (query.state.data?.exists ? 5000 : false),
    // We assume the token exists until we evidence otherwise
    initialData: () => ({
      exists: true,
    }),
  });

  return data.exists;
}
